import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentPlanInstalmentReschedulePreviewDTO, PaymentPlanInstalmentsApi, PaymentPlansApi } from '@reposit/api-client';
import moment from 'moment';
import { AppDispatch } from '../..';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { fetchClaimRequested } from '../claim/claim.actions';
import { setFlashMessage } from '../flash-messages/flash-messages.actions';
import { AppState } from '../root.reducer';
import {
  createStandardAxiosInstance,
  createStandardPaymentPlanInstalmentsApi,
  createStandardPaymentPlansApi,
  runThunkWithAuth,
} from '../utils/api.utils';
import {
  CREATE_PAYMENT_PLAN_STORE_KEY,
  DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  DELETE_PAYMENT_PLAN_STORE_KEY,
  deletePaymentPlanInstalmentApiSuccess,
  FORCE_DELETE_PAYMENT_PLAN_STORE_KEY,
  PAYMENT_PLAN_INSTALMENT_DESIGNER_STORE_KEY,
  RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  UPDATE_PAYMENT_PLAN_TENANT_DEFERRAL_STORE_KEY,
} from './payment-plan.actions';
import {
  ApplyPaymentPlanInstalmentDesignerCsvPayload,
  ConfirmPaymentPlanInstalmentReschedulePayload,
  CreatePaymentPlanPayload,
  DeletePaymentPlanInstalmentPayload,
  DeletePaymentPlanPayload,
  PaymentPlanInstalmentDesignerCsvPayload,
  PaymentPlanInstalmentDesignerPreview,
  PaymentPlanInstalmentReschedulePayload,
  UpdatePaymentPlanTenantDeferralPayload,
} from './payment-plan.types';

const postPaymentPlanInstalmentDesignerCsv = async <T>(
  state: AppState,
  dispatch: AppDispatch,
  paymentPlanId: string,
  file: File,
  action: 'preview' | 'apply',
  extraBody: Record<string, unknown> = {},
): Promise<T> => {
  const api = createStandardAxiosInstance(state, dispatch);
  const csv = await file.text();

  const result = await runThunkWithAuth(
    () => api.post(`/v2/payment-plan/${paymentPlanId}/instalment-designer/${action}`, { csv, ...extraBody }),
    dispatch,
  );
  return result.data;
};

export const createPaymentPlanThunk = createAsyncThunk<
  void,
  CreatePaymentPlanPayload,
  {
    state: AppState;
  }
>('payment-plan/create', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlansApi = createStandardPaymentPlansApi(state, dispatch);
    const {
      startDate,
      amountPerInstalment,
      orderCustomerId,
      claimId,
      manualPaymentsTotal,
      isHistoric,
      interestAmount,
      productType,
    } = payload;

    await runThunkWithAuth(
      () =>
        api.createPaymentPlan({
          startDate: moment(startDate).format('YYYY-MM-DD'),
          amountPerInstalment,
          orderCustomerId,
          manualPaymentsTotal,
          isHistoric,
          interestAmount,
          productType,
        }),
      dispatch,
    );

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: CREATE_PAYMENT_PLAN_STORE_KEY,
        message: `Success! Payment plan created for claim ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: CREATE_PAYMENT_PLAN_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
});

export const deletePaymentPlanThunk = createAsyncThunk<
  void,
  DeletePaymentPlanPayload,
  {
    state: AppState;
  }
>('payment-plan/delete', async (payload, thunkAPI) => {
  const { claimId, paymentPlanId } = payload;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlansApi = createStandardPaymentPlansApi(state, dispatch);

    await runThunkWithAuth(() => api.deletePaymentPlanById(paymentPlanId), dispatch);

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: DELETE_PAYMENT_PLAN_STORE_KEY,
        message: `Success! Payment plan deleted for claim ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: DELETE_PAYMENT_PLAN_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    throw e;
  }
});

export const forceDeletePaymentPlanThunk = createAsyncThunk<
  void,
  DeletePaymentPlanPayload,
  {
    state: AppState;
  }
>('payment-plan/force-delete', async (payload, thunkAPI) => {
  const { claimId, paymentPlanId } = payload;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlansApi = createStandardPaymentPlansApi(state, dispatch);

    await runThunkWithAuth(() => api.forceDeletePaymentPlanById(paymentPlanId), dispatch);

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: FORCE_DELETE_PAYMENT_PLAN_STORE_KEY,
        message: `Success! Payment plan force deleted for claim ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: FORCE_DELETE_PAYMENT_PLAN_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    throw e;
  }
});

export const deletePaymentPlanInstalmentThunk = createAsyncThunk<
  void,
  DeletePaymentPlanInstalmentPayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('payment-plan/delete-instalment', async (payload, thunkAPI) => {
  const { claimId, paymentPlanId, instalmentId } = payload;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlanInstalmentsApi = createStandardPaymentPlanInstalmentsApi(state, dispatch);

    await runThunkWithAuth(() => api.deletePaymentPlanInstalment(paymentPlanId, instalmentId), dispatch);

    dispatch(deletePaymentPlanInstalmentApiSuccess({ paymentPlanId, instalmentId }));
    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
        message: `Success! Payment plan instalment deleted for claim ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});

export const previewPaymentPlanInstalmentRescheduleThunk = createAsyncThunk<
  PaymentPlanInstalmentReschedulePreviewDTO,
  PaymentPlanInstalmentReschedulePayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('payment-plan/preview-instalment-reschedule', async (payload, thunkAPI) => {
  const { paymentPlanId, instalmentId, rescheduledDate } = payload;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlanInstalmentsApi = createStandardPaymentPlanInstalmentsApi(state, dispatch);

    const result = await runThunkWithAuth(
      () => api.previewReschedulePaymentPlanInstalment(paymentPlanId, instalmentId, { rescheduledDate }),
      dispatch,
    );

    return result.data;
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});

export const reschedulePaymentPlanInstalmentThunk = createAsyncThunk<
  PaymentPlanInstalmentReschedulePreviewDTO,
  ConfirmPaymentPlanInstalmentReschedulePayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('payment-plan/reschedule-instalment', async (payload, thunkAPI) => {
  const { claimId, paymentPlanId, instalmentId, rescheduledDate, expectedCurrentScheduleHash } = payload;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlanInstalmentsApi = createStandardPaymentPlanInstalmentsApi(state, dispatch);

    const result = await runThunkWithAuth(
      () => api.reschedulePaymentPlanInstalment(paymentPlanId, instalmentId, { rescheduledDate, expectedCurrentScheduleHash }),
      dispatch,
    );

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
        message: `Success! Payment plan instalment rescheduled for claim ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );

    return result.data;
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});

export const downloadPaymentPlanInstalmentDesignerCsvThunk = createAsyncThunk<
  void,
  { paymentPlanId: string },
  {
    state: AppState;
  }
>('payment-plan-instalment-designer/download-csv', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api = createStandardAxiosInstance(state, dispatch);
    const result = await runThunkWithAuth(
      () => api.get(`/v2/payment-plan/${payload.paymentPlanId}/instalment-designer/csv`, { responseType: 'blob' }),
      dispatch,
    );
    const fileBlob = new Blob([result.data], { type: 'text/csv' });
    const url = URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-plan-${payload.paymentPlanId}-pending-instalments.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: PAYMENT_PLAN_INSTALMENT_DESIGNER_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    throw e;
  }
});

export const previewPaymentPlanInstalmentDesignerCsvThunk = createAsyncThunk<
  PaymentPlanInstalmentDesignerPreview,
  PaymentPlanInstalmentDesignerCsvPayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('payment-plan-instalment-designer/preview-csv', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    return await postPaymentPlanInstalmentDesignerCsv<PaymentPlanInstalmentDesignerPreview>(
      state,
      dispatch,
      payload.paymentPlanId,
      payload.file,
      'preview',
    );
  } catch (e) {
    // Surfaced inline in the designer modal via rejectWithValue; no flash to avoid duplicate errors.
    return thunkAPI.rejectWithValue(getErrorMessage(e));
  }
});

export const applyPaymentPlanInstalmentDesignerCsvThunk = createAsyncThunk<
  PaymentPlanInstalmentDesignerPreview,
  ApplyPaymentPlanInstalmentDesignerCsvPayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('payment-plan-instalment-designer/apply-csv', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const result = await postPaymentPlanInstalmentDesignerCsv<PaymentPlanInstalmentDesignerPreview>(
      state,
      dispatch,
      payload.paymentPlanId,
      payload.file,
      'apply',
      {
        expectedCurrentPaymentPlanTotal: payload.expectedCurrentPaymentPlanTotal,
        expectedCurrentScheduleHash: payload.expectedCurrentScheduleHash,
      },
    );
    dispatch(fetchClaimRequested({ claimId: payload.claimId }));
    dispatch(
      setFlashMessage({
        key: PAYMENT_PLAN_INSTALMENT_DESIGNER_STORE_KEY,
        message: result.reinstatesPaymentPlan
          ? `Success! Payment plan reinstated for claim ${payload.claimId}`
          : `Success! Payment plan instalments modified for claim ${payload.claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
    return result;
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: PAYMENT_PLAN_INSTALMENT_DESIGNER_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});

export const updatePaymentPlanTenantDeferralThunk = createAsyncThunk<
  void,
  UpdatePaymentPlanTenantDeferralPayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('payment-plan/update-tenant-deferral', async (payload, thunkAPI) => {
  const { claimId, paymentPlanId, enabled } = payload;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: PaymentPlansApi = createStandardPaymentPlansApi(state, dispatch);

    await runThunkWithAuth(() => api.updatePaymentPlanTenantDeferral(paymentPlanId, { enabled }), dispatch);

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: UPDATE_PAYMENT_PLAN_TENANT_DEFERRAL_STORE_KEY,
        message: enabled
          ? `Success! Tenant deferral enabled for claim ${claimId}`
          : `Success! Tenant deferral disabled for claim ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: UPDATE_PAYMENT_PLAN_TENANT_DEFERRAL_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});
